import { useState, useRef } from "react";
import { uploadImage } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import { authedFileUrl } from "@/lib/files";
import { toast } from "sonner";
import { UploadSimple, X, Image as ImageIcon } from "@phosphor-icons/react";

// Multi-image uploader for damage reports (up to 5 images).
// Value: array of Cloud Storage download URLs (strings)
export default function MultiImageUploader({ value, onChange, max = 5, testId = "multi-image-uploader" }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const paths = value || [];

  const pick = () => inputRef.current?.click();

  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = Math.max(0, max - paths.length);
    if (allowed === 0) {
      toast.error(`Max ${max} photos`);
      return;
    }
    const toUpload = files.slice(0, allowed);
    setUploading(true);
    const newPaths = [...paths];
    for (const f of toUpload) {
      try {
        const url = await uploadImage(f, user.id);
        newPaths.push(url);
      } catch (err) {
        toast.error(err.message || "Upload failed");
      }
    }
    setUploading(false);
    onChange(newPaths);
    e.target.value = "";
  };

  const remove = (idx) => {
    const next = paths.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={handleChange}
        data-testid={`${testId}-input`}
      />
      <div className="flex flex-wrap gap-2 mb-2">
        {paths.map((p, i) => (
          <div key={p} className="relative w-20 h-20 border border-[#27272A] group">
            <img src={authedFileUrl(p)} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`${testId}-remove-${i}`}
              className="absolute -top-1 -right-1 w-5 h-5 bg-[#D32F2F] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ))}
        {paths.length < max && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            data-testid={`${testId}-add`}
            className="w-20 h-20 border border-dashed border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] text-[#52525B] flex flex-col items-center justify-center gap-1 transition-colors duration-150"
          >
            {uploading ? (
              <span className="text-[10px] font-mono blink">...</span>
            ) : (
              <>
                <UploadSimple size={16} weight="bold" />
                <span className="text-[9px] font-mono uppercase">Add</span>
              </>
            )}
          </button>
        )}
      </div>
      <div className="text-[10px] font-mono text-[#52525B]">
        {paths.length}/{max} photos · JPG/PNG/WEBP · max 5MB each
      </div>
    </div>
  );
}
